<template>
    <section class="test-drive">
        <div class="select-dealer__container container">
            <StepBar :step="3" step-name="Select a dealer" />
            <Navigation :go-to-step="{ name: 'select-options', params: {brand: brand, urlModel: this.urlModel}}" />

            <div class="test-drive__content">
                <div class="select-dealer__content-form">
                    <h2 class="title">Please select a dealership</h2>

                    <div class="select-dealer__content-search-container">
                        <p class="select-dealer__content-search-title">Search for your dealer</p>
                        <SearchDealer @postcodeSearch="updatePostcodeSearch" @place-updated="handlePlaceUpdate" />
                    </div>

                    <template v-if="postcodeSearch === true">
                        <div class="select-dealer__content-grid">
                            <div class="card" v-for="dealership in closestDealers" :key="dealership.id">
                                <div class="card__inner select-dealer-card">
                                    <div class="select-dealer-card__address">
                                        <h3 class="select-dealer-card__title">Dealer Address</h3>
                                        <p class="select-dealer-card__info">
                                            {{ dealership.name }} <br />
                                            {{ dealership.address }} <br />
                                            {{ dealership.postcode }}
                                        </p>
                                    </div>

                                    <div class="select-dealer-card__logo">
                                        <picture v-if="dealership.brand == 'abarth' || dealership.brand == 'alfa-romeo'">
                                            <source type="image/webp" :srcset="`/img/logos/borderless/${dealership.brand}.webp, /img/logos/borderless/${dealership.brand}@2x.webp 2x`">
                                            <img class="img-responsive" :src="`/img/logos/borderless/${dealership.brand}.jpg, /img/logos/borderless/${dealership.brand}@2x.jpg 2x`" :alt="dealership.brand">
                                        </picture>
                                        <img v-else class="img-responsive" :src="brandLogoPath(`/img/logos/borderless/${dealership.brand}.svg`)" :alt="dealership.brand" />
                                    </div>

                                    <button type="button" class="btn btn--primary-inverted select-dealer-card__cta" @click="selectDealer(dealership)">
                                        Select this dealer
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div class="select-dealer__content-load-container">
                            <button type="button" class="link link--is-button" @click="showMoreDealers" v-if="showLoadMoreLink">Load more...</button>
                        </div>
                    </template>
                    <template v-else>
                        <div class="select-dealer__help-container">
                            <img src="/images/info.svg" alt="" class="select-dealer__help-icon" />
                            <p class="select-dealer__help-text">Enter your town/postcode above and choose your address from the list in order to find your nearest dealership</p>
                        </div>
                    </template>
                </div>

                <div class="select-dealer__content-info">
                    <picture v-if="model.image">
                        <source v-if="model.image_webp" :srcset="model.image_webp" type="image/webp" />
                        <img class="img-responsive select-dealer__content-image" :src="model.image" :alt="model.model_title" />
                    </picture>

                    <div class="select-dealer__content-details">
                        <h3 class="capitalise">{{ model.model_title }}</h3>
                        <p class="extras">
                            <span>Engine:</span> {{ fuelChoice }} <br />
                            <span>Transmission:</span> {{ transmissionChoice.toLowerCase() }}
                        </p>

                        <p v-if="notes !== ''">
                            <span>Notes:</span> <br />
                            {{ notes }}
                        </p>
                    </div>

                    <div class="select-dealer__content-fix-spacing">
                        <Navigation :go-to-step="{ name: 'select-model', params: {brand: brand}}" link-text="Change model" />
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import StepBar from "../components/StepBar";
import Navigation from "../components/Navigation";
import SearchDealer from "../components/SearchDealer";
import { mapState, mapActions } from "vuex";
import EventObject from "../../modules/tracking/EventObject";
import brandLogoPath from '../../modules/brandLogoPath';

export default {
    props: {
        brand: {
            required: true,
        },
        urlModel: {
            required: true,
        },
    },
    components: {
        StepBar,
        Navigation,
        SearchDealer,
    },
    data() {
        return {
            model: this.$store.state.model,
            dealersToShow: 4,
            postcodeSearch: 0,
            loading: true,
            ignoreAvailability: false,
            availableDealerIds: [],
        };
    },
    mounted() {
        if (this.step3Page === true) {
            new EventObject("New Car - Test Drive Form", "Revisit", "Step 3 Page").dispatch();
        } else {
            new EventObject("New Car - Test Drive Form", "Visited", "Step 3 Page").dispatch();
            this.$store.commit("setStep3Page", true);
        }
    },
    created() {
        if (this.haveMinimumData == false) {
            this.$router.push({ name: 'select-options', params: {brand: brand, urlModel: this.urlModel}});
            return;
        }

        this.loading = true;
        this.initializeLocation({
            getAddressInfo: true,
            getPhoneInfo: true,
        });
    },
    methods: {
        brandLogoPath,
        ...mapActions("Dealers", ["initializeLocation"]),

        updatePostcodeSearch(value) {
            this.postcodeSearch = value;
            this.dealersToShow = 4;
        },

        handlePlaceUpdate(place) {
            if (typeof place.formatted_address != "undefined") {
                this.$store.commit("setCustomerAddress", { customerAddress: place.formatted_address });
            }
        },

        showMoreDealers() {
            this.dealersToShow += 4;
        },

        selectDealer(dealership) {
            this.$store.commit("setDealer", { dealer: dealership });
            this.$router.push({
                name: "select-time",
                params: {
                    brand: this.brand,
                    urlModel: this.urlModel,
                },
            });
        },
    },
    computed: {
        ...mapState("Dealers", ["dealers"]),
        ...mapState(["step3Page", "fuelChoice", "transmissionChoice", "notes"]),

        haveMinimumData() {
            if (this.fuelChoice == '') {
                return false;
            }
            if (this.transmissionChoice == '') {
                return false;
            }
            return true;
        },

        filteredDealers() {
            const filterBrand = (this.brand !== "") ? this.brand.toLowerCase() : window.brand.toLowerCase();
            return this.dealers.filter((dealer) => {
                // if (this.ignoreAvailability == false && this.availableDealerIds.indexOf(dealer.id) === -1) {
                //     return false;
                // }
                return dealer.offers_new === "Y" && dealer.brand.toLowerCase() === filterBrand;
            });
        },

        closestDealers() {
            return this.filteredDealers.slice(0, this.dealersToShow);
        },

        showLoadMoreLink() {
            return this.closestDealers.length < this.filteredDealers.length;
        },
    },
};
</script>